import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { inboundUnitCipher } from "@src/context/UnitsContext";
import { EnergyStarWasteUtility } from "@src/types";
import { useQueryClient } from "@tanstack/react-query";

import FetchClients, { ClientOutput } from "../client";

export type UtilityAccount =
  ClientOutput["utilities"]["UtilityAccountAutomatedBills"];

export type UtilityAccountNumberRequest =
  ClientOutput["utilities"]["UtilityAccountNumberRequest"];

export type UtilityAccountNameRequest =
  ClientOutput["utilities"]["UtilityAccountNameRequest"];

type AccountUtilityType = ClientOutput["utilities"]["AccountUtilityType"];

const accounts = {
  useQueryAll,
  usePostByAccountNumber,
  useDeleteByAccountNumber,
  useInvalidateQueryAll,
  usePatch,
  useQueryUtilityTypes,
  useQueryWasteType,
} as const;

export default accounts;

export const UTILITY_ACCOUNTS_KEY = "utilities/accounts";

// endpoint requires {feature: "Utilities", access: "read"}
async function getAccounts(
  siteId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.utilities.GET(
    "/{site_id}/accounts",
    {
      params: {
        path: {
          site_id: siteId,
        },
      },
      headers: checkFeatureAccessHeader,
    }
  );
  if (data) return [...data];
  throw new Error(error);
}

function useQueryAll(siteId: number) {
  return useQueryWithAccessCheckLog(
    ["Utilities", "read"],
    (checkFeatureAccessHeader) => getAccounts(siteId, checkFeatureAccessHeader),
    {
      queryKey: [UTILITY_ACCOUNTS_KEY, siteId],
    }
  );
}

// endpoint requires {feature: "Utilities", access: "edit"}
async function postAccountByAccountNumber(
  siteId: number,
  account: UtilityAccountNumberRequest,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.utilities.POST(
    "/{site_id}/accounts",
    {
      params: {
        path: {
          site_id: siteId,
        },
      },
      body: account,
      headers: checkFeatureAccessHeader,
    }
  );
  if (data) return data;
  throw new Error(error);
}

// This is for creating third-party-portal accounts only
function usePostByAccountNumber(siteId: number) {
  const queryClient = useQueryClient();

  return useMutationWithAccessCheckLog(
    ["Utilities", "edit"],
    (account: UtilityAccountNumberRequest, checkFeatureAccessHeader) =>
      postAccountByAccountNumber(siteId, account, checkFeatureAccessHeader),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [UTILITY_ACCOUNTS_KEY, siteId],
        });
      },
    }
  );
}

// endpoint requires {feature: "Utilities", access: "edit"}
async function deleteAccountByAccountNumber(
  siteId: number,
  accountId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.utilities.DELETE(
    "/{site_id}/accounts/{account_id}",
    {
      params: {
        path: {
          site_id: siteId,
          account_id: accountId,
        },
      },
      headers: checkFeatureAccessHeader,
    }
  );
  if (data) return data;
  throw new Error(error);
}

// This is for deleting third-party-portal accounts only
function useDeleteByAccountNumber(siteId: number) {
  const queryClient = useQueryClient();

  return useMutationWithAccessCheckLog(
    ["Utilities", "edit"],
    (accountId: number, checkFeatureAccessHeader) =>
      deleteAccountByAccountNumber(siteId, accountId, checkFeatureAccessHeader),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [UTILITY_ACCOUNTS_KEY, siteId],
        });
      },
    }
  );
}

// endpoint requires {feature: "Utilities", access: "edit"}
async function patchAccountName(
  siteId: number,
  { accountId, accountName }: { accountId: number; accountName: string },
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.utilities.PATCH(
    "/{site_id}/accounts/{account_id}",
    {
      params: {
        path: {
          site_id: siteId,
          account_id: accountId,
        },
      },
      body: {
        account_name: accountName,
      },
      headers: checkFeatureAccessHeader,
    }
  );
  if (data) return data;
  throw new Error(error);
}

function usePatch(siteId: number) {
  const queryClient = useQueryClient();

  return useMutationWithAccessCheckLog(
    ["Utilities", "edit"],
    (
      params: { accountId: number; accountName: string },
      checkFeatureAccessHeader
    ) => patchAccountName(siteId, params, checkFeatureAccessHeader),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [UTILITY_ACCOUNTS_KEY, siteId],
        });
      },
    }
  );
}

function useInvalidateQueryAll(siteId: number) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({
      queryKey: [UTILITY_ACCOUNTS_KEY, siteId],
    });
  };
}

// endpoint requires {feature: "Utilities", access: "read"}
async function getUtilityTypes(
  siteId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.utilities.GET(
    "/{site_id}/accounts/utility_types",
    {
      params: {
        path: {
          site_id: siteId,
        },
      },
      headers: checkFeatureAccessHeader,
    }
  );
  if (data) return data;
  throw new Error(error);
}

function useQueryUtilityTypes(siteId: number, enabled = true) {
  return useQueryWithAccessCheckLog<AccountUtilityType[]>(
    ["Utilities", "read"],
    (checkFeatureAccessHeader) =>
      getUtilityTypes(siteId, checkFeatureAccessHeader),
    {
      queryKey: [UTILITY_ACCOUNTS_KEY, "utilityTypes", siteId],
      enabled,
    }
  );
}

function useQueryWasteType(siteId: number) {
  return useQueryWithAccessCheckLog(
    ["Utilities", "read"],
    (checkFeatureAccessHeader) =>
      getUtilityTypes(siteId, checkFeatureAccessHeader),
    {
      queryKey: [UTILITY_ACCOUNTS_KEY, "utilityTypes", siteId],
      select: (utilityTypes) => {
        const wasteType = utilityTypes.find(({ name }) => name === "waste");
        if (!wasteType) {
          return undefined;
        }
        return {
          name: "waste",
          defaultUnitName: inboundUnitCipher[wasteType.default_unit_name],
          defaultUnitId: wasteType.default_unit_id,
        } as EnergyStarWasteUtility;
      },
    }
  );
}
